import PlanChange from "../PlanChange";
import Model from "../Model";
import ModelStore from "../../stores/ModelStore";
import Schedule, {Frequency} from "../Schedule";
import Expense, {ExpenseCategory} from "../Expense";
import Income, {IncomeType} from "../Income";
import Deduction, {DeductionType} from "../Deduction";
import {createUUID, dateMin, getISODateFromDate, getISODateTime, isoToLocalDate} from "../../stores/StoreUtilities";
import {MilestoneId} from "../Milestone";
import {addYears} from "date-fns";

export const OutsideEstateId = "__OUTSIDE_ESTATE__"
export const StandardTerms = [10, 15, 20, 25, 30]

export interface ILifeInsurancePlan {
  ownerId: string
  coverageAmount: number
  startDate?: string
  termYears?: number
  coverageEndDate?: string
  premiumAmount?: number
  premiumFrequency?: Frequency
  depositAssetId?: string
  notes?: string
}

export interface ILifeInsuranceStrategyChange {
  plans: ILifeInsurancePlan[]
}

export function computeEndDate(startDate: string | undefined, termYears: number | undefined): string | undefined {
  if (!startDate || !termYears) return undefined
  try {
    const date = isoToLocalDate(startDate)
    return getISODateFromDate(addYears(date, termYears))
  } catch {
    return undefined
  }
}

class LifeInsuranceStrategyChange extends PlanChange implements ILifeInsuranceStrategyChange {
  plans: ILifeInsurancePlan[]

  constructor(data: any) {
    super(data)
    const parsed = this.details ? JSON.parse(this.details) : {}
    const rawPlans: any[] = parsed?.plans ?? []

    this.plans = rawPlans
      .filter((p: any) => p.strategyType !== "None")
      .map((p: any) => {
        const plan: ILifeInsurancePlan = {
          ownerId: p.ownerId,
          coverageAmount: p.coverageAmount ?? 0,
          startDate: p.startDate,
          termYears: p.termYears,
          coverageEndDate: p.coverageEndDate,
          premiumAmount: p.premiumAmount,
          premiumFrequency: p.premiumFrequency,
          depositAssetId: p.depositAssetId,
          notes: p.notes,
        }
        if (plan.depositAssetId === undefined && p.beneficiaryType === "Other") {
          plan.depositAssetId = OutsideEstateId
        }
        return plan
      })
  }

  apply(model: Model) {
    const isoDateTime = getISODateTime()

    this.plans.forEach((plan: ILifeInsurancePlan, index: number) => {
      const owner = model.getOwner(plan.ownerId)
      if (!owner) return

      const coverageEnd = plan.coverageEndDate ?? computeEndDate(plan.startDate, plan.termYears)
      const ownerMilestoneId = owner.id === model.person2?.id
        ? MilestoneId.lifeExpectancy2
        : MilestoneId.lifeExpectancy1

      // Premium expense: runs from start until the earlier of coverage end or owner's death
      if (plan.premiumAmount && plan.premiumFrequency) {
        const milestoneDate = model.getDate(ownerMilestoneId)
        const coverageEndDate = coverageEnd ? model.getDate(coverageEnd) : undefined
        const earliest = dateMin(milestoneDate, coverageEndDate)
        const premiumEnd = earliest === milestoneDate ? ownerMilestoneId : coverageEnd

        const expense = new Expense({
          id: createUUID(),
          createdAt: isoDateTime, updatedAt: isoDateTime,
          accountId: model.accountId, userId: model.userId, modelId: model.id, model: model,
          expenseCategory: ExpenseCategory.InsuranceAndMedical,
          description: `${owner.nickname}'s Life Insurance Premium`,
          amount: plan.premiumAmount,
          annualInf: 0, infLock: true, discretionary: false,
          ownerId: plan.ownerId, owner: owner,
          schedule: Schedule.getDefaultSchedule(plan.premiumFrequency),
          start: plan.startDate, end: premiumEnd,
          sortOrder: model.expenses.length > 0 ? model.expenses[model.expenses.length-1].sortOrder + ModelStore.orderInterval : 0,
        })
        model.expenses.push(expense)
        model.addLock(expense.id, "*", "Locked by Life Insurance Strategy")
      }

      // Death benefit: deposit into target asset if policy is active at death
      if (!plan.coverageAmount) return
      if (!plan.depositAssetId || plan.depositAssetId === OutsideEstateId) return

      // Only pay death benefit if owner dies within the coverage period
      const deathDate = model.getDate(ownerMilestoneId)
      const coverageEndDate = coverageEnd ? model.getDate(coverageEnd) : undefined
      if (deathDate && coverageEndDate && deathDate > coverageEndDate) return

      const spouse = owner.spouse
      const beneficiary = spouse ?? owner
      const onceSchedule = Schedule.getDefaultSchedule("Once")

      // Non-taxable income (shows the benefit in gross income)
      // start = milestone so it fires at life expectancy; end = coverageEnd
      // so it only fires if death occurs within the coverage period
      const income = new Income({
        id: createUUID(),
        createdAt: isoDateTime, updatedAt: isoDateTime,
        accountId: model.accountId, userId: model.userId, modelId: model.id, model: model,
        incomeType: IncomeType.None,
        description: `${owner.nickname}'s Life Insurance - Death Benefit`,
        amount: plan.coverageAmount,
        annualInf: 0, infLock: true,
        ownerId: beneficiary.id, owner: beneficiary,
        schedule: onceSchedule, start: ownerMilestoneId,
        sortOrder: model.incomes.length > 0 ? model.incomes[model.incomes.length-1].sortOrder + ModelStore.orderInterval : 0,
      })
      model.incomes.push(income)
      model.addLock(income.id, "*", "Locked by Life Insurance Strategy")

      // Paired deduction routes the benefit directly into the target asset's
      // balance (same mechanism the calculator uses for 401k contributions
      // and brokerage dividend reinvestment)
      const deduction = new Deduction({
        id: createUUID(),
        createdAt: isoDateTime, updatedAt: isoDateTime,
        accountId: model.accountId, userId: model.userId, modelId: model.id, model: model,
        description: `${owner.nickname}'s Life Insurance - Benefit Deposit`,
        amount: plan.coverageAmount,
        annualInf: 0, infLock: true,
        ownerId: beneficiary.id,
        assetId: plan.depositAssetId,
        schedule: onceSchedule, start: ownerMilestoneId, end: ownerMilestoneId,
        archived: false, deductionType: DeductionType.Other,
        sortOrder: model.deductions.length > 0 ? model.deductions[model.deductions.length-1].sortOrder + ModelStore.orderInterval : 0,
      })
      const asset = model.assets.find(a => a.id === plan.depositAssetId)
      if (asset) deduction.asset = asset
      model.deductions.push(deduction)
      model.addLock(deduction.id, "*", "Locked by Life Insurance Strategy")
    })
  }

  async commit(model: Model, modelStore: ModelStore) {
    // TODO
  }
}

export default LifeInsuranceStrategyChange
